import { useEffect } from 'react'
import Checkbox from '@mui/material/Checkbox'

import MenuItem from '@mui/material/MenuItem'
import ListItemText from '@mui/material/ListItemText'
import Select from '@mui/material/Select'
import {
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemIcon
} from '@mui/material'
import React from 'react'
import { Control, Controller, FieldValues } from 'react-hook-form'
import useLoadVocabulary from '../../../hooks/useLoadVocabulary'
import { KeyValPair } from '../../../types/Common'

interface Props<TFieldValues extends FieldValues = FieldValues> {
  vocabularyKey?: string
  placeholder: string
  controlName: string
  control: Control<TFieldValues>
  staticData?: KeyValPair[]
  label: string
  fieldNames?: { fieldValue?: string; fieldLabel?: string }
  error?: boolean
  required?: boolean
  ref?: React.ForwardedRef<unknown>
}

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8

const MultiSelect = <TFieldValues extends FieldValues = FieldValues>({
  vocabularyKey,
  placeholder,
  staticData,
  fieldNames: { fieldValue = '_id', fieldLabel = 'title' } = {},
  control: typedControl,
  controlName,
  label,
  error,
  required = false,
  ref
}: Props<TFieldValues>) => {
  const control = typedControl as unknown as Control<FieldValues>

  const {
    load,
    loaded,
    loading,
    data = [],
    setData
  } = useLoadVocabulary(vocabularyKey)

  useEffect(() => {
    if (staticData) {
      setData(staticData)
    }
  }, [staticData, setData])

  return (
    <FormControl
      error={error}
      variant='standard'
      fullWidth={true}
      required={required}
    >
      <InputLabel id={`${controlName}-label`}>{label}</InputLabel>
      <Controller
        name={controlName}
        defaultValue={[]}
        render={({ field, fieldState: { error } }) => (
          <>
            <Select
              {...field}
              label={label}
              labelId={`${controlName}-label`}
              onChange={(event) => {
                const value = event.target.value

                field.onChange(value)
              }}
              ref={ref}
              fullWidth={true}
              multiple
              value={field.value}
              onOpen={() => {
                if (!staticData && !loaded && !loading) {
                  load()
                }
              }}
              renderValue={(selected) => {
                return selected?.length
                  ? selected
                      .map(
                        (s: any) =>
                          data.find((d) => d[fieldValue] === s)![fieldLabel]
                      )
                      .join(', ')
                  : placeholder
              }}
              MenuProps={{
                autoFocus: false,
                PaperProps: {
                  style: {
                    maxHeight: ITEM_HEIGHT * 6.5 + ITEM_PADDING_TOP
                  }
                }
              }}
            >
              {loading
                ? [
                    <MenuItem key='loading' value='loading'>
                      <ListItemText primary='Loading...' />
                    </MenuItem>
                  ]
                : [
                    data.map((option) => (
                      <MenuItem
                        key={option[fieldValue]}
                        value={option[fieldValue]}
                      >
                        <ListItemIcon>
                          <Checkbox
                            checked={
                              field.value.indexOf(option[fieldValue]) > -1
                            }
                          />
                        </ListItemIcon>
                        <ListItemText primary={option[fieldLabel]} />
                      </MenuItem>
                    ))
                  ]}
            </Select>
            <FormHelperText>{error?.message || ' '}</FormHelperText>
          </>
        )}
        control={control}
      ></Controller>
    </FormControl>
  )
}

MultiSelect.displayName = 'MultiSelect'

export default MultiSelect
